import React, { useState, useEffect, useMemo } from 'react';
import { Container, Card, Row, Col, Spinner, Alert, Table, Button } from 'react-bootstrap';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { useLocation, useSearch } from 'wouter';
import ClassSelector from './ClassSelector';
import DateRangePicker from './DateRangePicker';
import { getClassAttendance, ClassAttendanceData, StudentRecord } from '../../services/api';
import { formatRelativeWithDate } from '../../utils/dateUtils';
import { convertToCSV, downloadCSV, generateFilename } from '../../utils/csvUtils';

interface ClassAttendanceReportProps {
  className?: string;
}

const ClassAttendanceReport: React.FC<ClassAttendanceReportProps> = ({ className }) => {
  const [attendanceData, setAttendanceData] = useState<ClassAttendanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setLocation] = useLocation();
  const searchParams = useSearch();

  // Calculate default date range (last 30 days)
  const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  };

  // Parse query parameters for dates
  const getInitialDates = () => {
    const params = new URLSearchParams(searchParams);
    const fromParam = params.get('from');
    const toParam = params.get('to');

    const defaults = getDefaultDates();

    return {
      startDate: fromParam || defaults.startDate,
      endDate: toParam || defaults.endDate,
    };
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.startDate);
  const [endDate, setEndDate] = useState(initialDates.endDate);

  // Update URL when dates change
  const updateDateInURL = (from: string, to: string) => {
    if (!className) return;
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    setLocation(`/attendance/class/${encodeURIComponent(className)}?${params.toString()}`, { replace: true });
  };

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    updateDateInURL(date, endDate);
  };

  const handleEndDateChange = (date: string) => {
    setEndDate(date);
    updateDateInURL(startDate, date);
  };

  // Fetch attendance data when className or dates change
  useEffect(() => {
    const fetchAttendanceData = async () => {
      if (!className) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getClassAttendance(className, startDate, endDate);
        setAttendanceData(data);
      } catch (err: any) {
        console.error('Error fetching attendance:', err);
        setError(err.response?.data?.detail || 'Failed to fetch attendance data');
      } finally {
        setLoading(false);
      }
    };

    if (className) {
      fetchAttendanceData();
    }
  }, [className, startDate, endDate]);


  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([]);

  // Calculate top 3 attendees
  interface TopAttendee {
    rank: number;
    student_name: string;
    attendance_count: number;
  }

  const topAttendeesData = useMemo(() => {
    if (!attendanceData) return [];

    // Count attendance per student
    const attendanceCounts: { [studentName: string]: number } = {};

    attendanceData.attendance_records.forEach((record) => {
      attendanceCounts[record.student_name] = (attendanceCounts[record.student_name] || 0) + 1;
    });

    // Convert to array and sort by count (descending), then by name (ascending)
    const sortedAttendees = Object.entries(attendanceCounts)
      .map(([name, count]) => ({ student_name: name, attendance_count: count }))
      .sort((a, b) => {
        if (b.attendance_count !== a.attendance_count) {
          return b.attendance_count - a.attendance_count;
        }
        return a.student_name.localeCompare(b.student_name);
      })
      .slice(0, 3)
      .map((attendee, index) => ({
        rank: index + 1,
        ...attendee,
      }));

    return sortedAttendees;
  }, [attendanceData]);

  // Define columns for the main attendance table
  const columnHelper = createColumnHelper<StudentRecord>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: 'Date',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('student_name', {
        header: 'Student Name',
        cell: info => info.getValue(),
      }),
    ],
    [columnHelper]
  );

  // Define columns for the top 3 attendees table
  const topAttendeesColumnHelper = createColumnHelper<TopAttendee>();
  const topAttendeesColumns = useMemo(
    () => [
      topAttendeesColumnHelper.accessor('rank', {
        header: 'Rank',
        cell: info => info.getValue(),
      }),
      topAttendeesColumnHelper.accessor('student_name', {
        header: 'Student Name',
        cell: info => info.getValue(),
      }),
      topAttendeesColumnHelper.accessor('attendance_count', {
        header: 'Attendance Count',
        cell: info => info.getValue(),
      }),
    ],
    [topAttendeesColumnHelper]
  );

  // Create table instance for main attendance records
  const table = useReactTable({
    data: attendanceData?.attendance_records || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Create table instance for top 3 attendees
  const topAttendeesTable = useReactTable({
    data: topAttendeesData,
    columns: topAttendeesColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Handle CSV export
  const handleExportCSV = () => {
    if (!attendanceData || attendanceData.attendance_records.length === 0) return;

    const csvHeaders = [
      { key: 'date' as keyof StudentRecord, label: 'Date' },
      { key: 'student_name' as keyof StudentRecord, label: 'Student Name' },
    ];

    const csvContent = convertToCSV(attendanceData.attendance_records, csvHeaders);
    const filename = generateFilename(
      `class_${attendanceData.class_name}`,
      startDate,
      endDate
    );

    downloadCSV(csvContent, filename);
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Attendance Report by Class{attendanceData && !loading && (<span>: {className}</span>)}</h1>

      <Row className="mb-4">
        <Col>
          <ClassSelector selectedClassName={className} />
        </Col>
        <Col>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </Col>
      </Row>


      {className && (
        <>
          {loading && (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="my-3">
              {error}
            </Alert>
          )}

          {attendanceData && !loading && (
            <>
              {/* Summary Section */}
              <Row className="mb-4">
                <Col md={4} className="mb-3">
                  <Card className="text-center h-100">
                    <Card.Body>
                      <Card.Title>Total Attendance</Card.Title>
                      <h1 className="display-3">{attendanceData.total_attendance}</h1>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} className="mb-3">
                  <Card className="text-center h-100">
                    <Card.Body>
                      <Card.Title>Last Class Date</Card.Title>
                      <h2 className="display-6 mt-3">
                        {formatRelativeWithDate(attendanceData.last_attendance_date)}
                      </h2>
                    </Card.Body>
                  </Card>
                </Col>
                {attendanceData.class_instance_summaries.map((summary, index) => (
                  <Col md={4} key={index} className="mb-3">
                    <Card className="text-center h-100">
                      <Card.Body>
                        <Card.Title>
                          {summary.day} {summary.time}
                        </Card.Title>
                        <h1 className="display-3">{summary.count}</h1>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Top 3 Attendees Card */}
              {topAttendeesData.length > 0 && (
                <Card className="mb-4">
                  <Card.Body>
                    <Card.Title className="mb-3">Top 3 Attendees</Card.Title>
                    <Table striped bordered hover variant="dark">
                      <thead>
                        {topAttendeesTable.getHeaderGroups().map(headerGroup => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <th key={header.id}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {topAttendeesTable.getRowModel().rows.map(row => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* Table Section */}
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Card.Title className="mb-0">Attendance Records</Card.Title>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleExportCSV}
                      disabled={!attendanceData || attendanceData.attendance_records.length === 0}
                    >
                      📥 Download CSV
                    </Button>
                  </div>
                  <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Table striped bordered hover variant="dark">
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        {table.getHeaderGroups().map(headerGroup => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => (
                              <th
                                key={header.id}
                                onClick={header.column.getToggleSortingHandler()}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: ' ▲',
                                  desc: ' ▼',
                                }[header.column.getIsSorted() as string] ?? null}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {table.getRowModel().rows.map(row => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </>
      )}

      {!className && !loading && (
        <Alert variant="info" className="mt-3">
          Please select a class to view the attendance report.
        </Alert>
      )}
    </Container>
  );
};

export default ClassAttendanceReport;
